import {
  Users,
  Calculator,
  Receipt,
  Database,
  Settings2,
  FileText,
  LayoutDashboard,
  PhoneCall,
  ClipboardCheck,
} from "lucide-react";

export const BRAND = {
  name: "Zippy Infosolution",
  tagline: "Reducing costs makes sense!",
  email: "info@zippyinfosolution.com",
  address: "Sivasagar, Assam, India",
  logo:
    "https://customer-assets.emergentagent.com/job_uk-infosolution-site/artifacts/88r33py2_Simple%20Z%20Letter%20Logo.png",
  founderPhoto: "/founder-afftab.png",
  founderLinkedin: "https://www.linkedin.com/in/afftab-ahmed-a33514143",
  heroImage: "/services/hero.jpg",
};

export const WHATSAPP = {
  display: "+91 88225 39911",
  link: "https://wa.me/918822539911",
};

export const SOCIALS = {
  facebook:
    "https://www.facebook.com/p/Zippy-Infosolution-Opc-PvtLtd-100090595334691/",
  linkedin: "https://in.linkedin.com/company/zippy-infosolution-pvt-ltd",
  instagram: "https://www.instagram.com/zippy_infosolution/",
};

export const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

export const FOUNDER_STATS = [
  ["13+", "Years Experience"],
  ["60%", "Avg. Cost Reduction"],
  ["2x", "Productivity Boost"],
  ["200+", "Businesses Helped"],
];

const IMG = (name) => `/services/${name}.jpg`;

export const SERVICES = [
  {
    icon: Users,
    title: "Virtual Assistant Services",
    desc: "Dedicated remote professionals who slot straight into your team to handle day-to-day tasks, so you can focus on growth.",
    features: ["Inbox & Calendar", "Task Coordination", "Travel Arrangements", "Personal Assistance"],
    image: IMG("virtual-assistant"),
  },
  {
    icon: Calculator,
    title: "Bookkeeping",
    desc: "Accurate, up-to-date books maintained by experienced bookkeepers — total clarity and compliance, every month.",
    features: ["Transaction Recording", "Bank Reconciliation", "Expense Tracking", "Ledger Management"],
    image: IMG("bookkeeping"),
  },
  {
    icon: Receipt,
    title: "Accounting Services",
    desc: "Expert financial management tailored to your business — from reporting to compliance, precise and audit-ready.",
    features: ["Financial Reporting", "Invoicing", "VAT & Tax Prep", "Payroll Support"],
    image: IMG("accounting"),
  },
  {
    icon: Database,
    title: "Data Entry",
    desc: "Fast, accurate data entry and management. We handle high-volume data with precision so your records stay clean.",
    features: ["CRM Data", "Spreadsheets", "Data Cleansing", "Database Updates"],
    image: IMG("data-entry"),
  },
  {
    icon: Settings2,
    title: "Back Office Support",
    desc: "The engine behind your operations — we manage the essential behind-the-scenes tasks that keep things running.",
    features: ["Process Management", "Order Processing", "Compliance", "Workflow Optimisation"],
    image: IMG("back-office"),
  },
  {
    icon: FileText,
    title: "Document Production",
    desc: "Professional document creation, formatting and management — from reports to presentations, delivered with care.",
    features: ["Document Creation", "Template Design", "Format Conversion", "Version Control"],
    image: IMG("document-production"),
  },
  {
    icon: LayoutDashboard,
    title: "Portal Management",
    desc: "Comprehensive digital portal administration — we keep your online systems running smoothly, securely and efficiently.",
    features: ["User Management", "Content Updates", "Access Control", "System Monitoring"],
    image: IMG("portal-management"),
  },
  {
    icon: PhoneCall,
    title: "Calling Support",
    desc: "Professional telephone answering and customer service — your business never misses an important call.",
    features: ["Call Handling", "Message Taking", "Appointment Scheduling", "Customer Support"],
    image: IMG("calling-support"),
  },
  {
    icon: ClipboardCheck,
    title: "General Administrative Support",
    desc: "Complete administrative assistance — from scheduling to research, we handle the details so you can focus on growth.",
    features: ["Email Management", "Calendar Management", "Research & Analysis", "General Admin"],
    image: IMG("admin-support"),
  },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "We work around your business hours",
    body: "Our teams align to your business hours, so support is live exactly when your customers and colleagues need it — no gaps, no waiting.",
  },
  {
    n: "02",
    title: "Security & privacy first",
    body: "Your data is handled with strict confidentiality, secure systems and clear access controls. Your reputation is safe with us.",
  },
  {
    n: "03",
    title: "Bespoke, never off-the-shelf",
    body: "No rigid packages. We build a support model around your goals, your tools and your budget — scaling up or down as you grow.",
  },
  {
    n: "04",
    title: "UK, India & global reach",
    body: "We proudly support UK businesses, alongside Indian and global companies, with pricing shaped to each campaign or process.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Zippy took over our inbound calls within a fortnight. Response times dropped and our team finally got their focus back.",
    name: "James Whitfield",
  },
  {
    quote:
      "The lead verification alone paid for itself. Our sales calls are now with people who actually want to buy.",
    name: "Priya Sharma",
  },
  {
    quote:
      "Their back-office admin team is faultless. Invoicing, scheduling and data entry are handled before I even ask.",
    name: "Daniel Osei",
  },
  {
    quote:
      "Portal management used to eat my week. Zippy keep our systems updated, secure and running without a hitch.",
    name: "Sarah Collins",
  },
  {
    quote:
      "Genuinely bespoke and reliable. They shaped the admin support around exactly how we work.",
    name: "Mohammed Iqbal",
  },
];

export const PRICING = {
  uk: {
    price: "£399",
    period: "/ month",
    note: "Starting price for UK businesses",
    features: [
      "Bespoke packages tailored to you",
      "Flexible terms & support periods",
      "No recruitment or overhead costs",
      "Transparent pricing, no hidden fees",
    ],
  },
  global:
    "We also support Indian-based and global companies. For these, pricing is fully bespoke depending on your campaign or process — get in touch for a tailored quote.",
};

export const ABOUT = {
  overline: "About us",
  title: "About Zippy Infosolution",
  paragraphs: [
    "Zippy Infosolution is a quality BPO and business-support partner helping companies grow revenue and cut operational costs. Team Zippy is right here to strengthen your business with dependable lead generation, calling and administrative services.",
    "From virtual assistants and bookkeeping to portal management and back-office support, we take the everyday workload off your plate so you can focus on what matters. We work around your business hours and treat the security and privacy of your data as a top priority.",
    "We proudly serve UK businesses with clear, affordable pricing, and we also partner with Indian and global companies on a bespoke basis.",
  ],
  highlights: [
    "Quality BPO Solutions For Businesses",
    "We work around your business hours",
    "Secure & confidential",
    "Bespoke, cost-effective plans",
  ],
};
